import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CombinedReducersType } from "@redux/reducer";
import {
  Accordion,
  ButtonIcon,
  Drawer,
  Keyline,
  LinkCell,
  LinkImage,
  LinkNavigation,
  LinkNavigationGroup,
  NavigationHeader
} from "@common/DynamicUikit";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import {
  gaSiteType,
  sendTrackEvent,
  transformBrandForGoogleAnalytics
} from "@util/analytics/utils";
import {
  getAboutAppURL,
  getBrandName,
  isPC,
  replacePlaceholder
} from "@util/helpers";
import { SB_ENV, TRACK_EVENT } from "@config/common";
import { BRAND_LIST, COUNTRY, LANGUAGE, RouterType } from "@type/index";
import { initTopPageAllData } from "../../App/TopStyling/stylingConfig";

const PublicHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const config = useSelector((store: CombinedReducersType) => store.config);
  const [openDrawer, setOpenDrawer] = useState(false);

  const handleOpen = () => {
    setOpenDrawer(true);
  };

  const handleClose = () => {
    setOpenDrawer(false);
  };

  const handleNavigate = (link: string) => {
    initTopPageAllData(dispatch);
    const url = generatePath(`/:region/:language/stylingbook/${link}/women`, {
      ...params
    } as RouterType);
    navigate(url);
    handleClose();
  };

  const UQDouble = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 90 40"
      width="90"
      height="40"
    >
      <title>{t("text_styling_book_go_to_top")}</title>
      <path fill="red" d="M50 0h40v40H50zM0 0h40v40H0z" />
      <g fill="#fff">
        <path d="M79.48 5.47h2.53v12.64h-2.53zM63.47 13.9a4.21 4.21 0 0 1-8.42 0V5.47h2.53v8.43a1.68 1.68 0 1 0 3.36 0V5.47h2.53zm11.79 20.63h-8.42V21.89h2.53V32h5.89v2.53zm0-16.42h-2.53l-3.36-7.22v7.22h-2.53V5.47h2.53l3.36 7.22V5.47h2.53v12.64zm-16 3.78a4.21 4.21 0 0 0-4.21 4.22v4.21a4.21 4.21 0 0 0 4.21 4.21 4.34 4.34 0 0 0 .82-.07l.86 2.6h2.53l-1.25-3.75a4.2 4.2 0 0 0 1.25-3v-4.2a4.21 4.21 0 0 0-4.21-4.22m1.68 8.43a1.68 1.68 0 1 1-3.36 0v-4.21a1.68 1.68 0 1 1 3.36 0zm19.8-8.43a4.22 4.22 0 0 0-4.22 4.22v4.21a4.21 4.21 0 0 0 8.42 0v-4.21a4.21 4.21 0 0 0-4.21-4.22m1.68 8.43a1.68 1.68 0 0 1-3.37 0v-4.21a1.68 1.68 0 0 1 3.37 0zM22.74 15.16H34.1v2.52H22.74zM24 5.47h8.84V8H24zm-9.26 0H7.15V8h5.06v7.16H5.9v2.52h11.36v-2.52h-2.52V5.47zm8 16.84v12.22H34.1V22.31zM31.57 32h-6.31v-7.16h6.31zM7.15 22.31l-1.28 6.12h2.52l.76-3.59h5.07L12.73 32H5.14l-.51 2.53h10.11l2.52-12.22H7.15z" />
      </g>
    </svg>
  );

  const GUIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 40"
      width="40"
      height="40"
    >
      <title>{t("text_styling_book_go_to_top")}</title>
      <path fill="#17288b" d="M0 0h40v40H0z" />
      <path
        d="M20.23 18.55h-8.6v2.91h5.64a5.83 5.83 0 1 1-1.79-5.82h3.71a8.92 8.92 0 1 0 1 2.91zm13.95-6.19v9.45a4 4 0 0 1-8 0v-9.45h-2.91v9.45a6.91 6.91 0 0 0 13.82 0v-9.45z"
        fill="#fff100"
      />
    </svg>
  );

  // Handle brand logo
  const handleBrandLogo = (brand: string) => {
    switch (brand) {
      case BRAND_LIST.UQ:
        return UQDouble;
      case BRAND_LIST.GU:
        return GUIcon;
      default:
        return UQDouble;
    }
  };

  // Handle switch language
  const handleSwitchLanguage = (lang: string) => {
    if (lang === config.language) {
      return;
    }
    if (lang === LANGUAGE.ZHHK) {
      const url = replacePlaceholder(window.location.href, {
        en_GB: LANGUAGE.ZHHK
      });
      window.location.replace(url);
    } else {
      const url = replacePlaceholder(window.location.href, {
        zh_HK: LANGUAGE.ENGB
      });
      window.location.replace(url);
    }
  };

  return (
    <div>
      <NavigationHeader
        contentArea={[
          <ButtonIcon
            element="button"
            icon="feature-menu"
            label={t("text_menu")}
            onClick={handleOpen}
            variant="dark"
          />
        ]}
        featureImage={[
          <LinkImage
            element="a"
            href={`/${config.region}/${config.language}/stylingbook`}
          >
            <img
              alt="StylingBook logo"
              src="https://asset.uniqlo.com/g/icons/stylingbook-logo_v2.svg"
            />
          </LinkImage>
        ]}
        linkImageArea={
          config.isApp
            ? [
                <LinkImage element="a">
                  {handleBrandLogo(config.brand_name)}
                </LinkImage>
              ]
            : [
                <LinkImage
                  element="a"
                  href={`${config.basePath}`}
                  gaCallback={() => {
                    // ga
                    sendTrackEvent({
                      brand: transformBrandForGoogleAnalytics(
                        config.brand_name
                      ),
                      event: TRACK_EVENT,
                      eventAction: `${getBrandName()} Logo`,
                      eventCategory: "header",
                      eventLabel: `Icon label ${getBrandName()} Logo`,
                      siteType: gaSiteType()
                    });
                  }}
                  style={{ zIndex: 1 }}
                >
                  {handleBrandLogo(config.brand_name)}
                </LinkImage>
              ]
        }
        sticky
      />
      <Drawer
        closeLabel={" "}
        onBackdropClick={handleClose}
        onRequestClose={handleClose}
        position="end"
        title=""
        visible
        isOpen={openDrawer}
      >
        <React.Fragment key="header-drawer">
          <Accordion
            element="fieldset"
            label={t("text_styling_book_header_title")}
            labelElement="legend"
            defaultExpanded
            variant="small"
          >
            <LinkNavigationGroup
              indented
              labelId={t("text_styling_book_header_title")}
              variant="vertical"
            >
              <LinkNavigation
                element="button"
                onClick={() => handleNavigate("officialstyling")}
              >
                {t("text_styling_book_official_styling")}
              </LinkNavigation>
              <LinkNavigation
                element="button"
                onClick={() => handleNavigate("stylehint")}
              >
                {t("text_styling_book_stylehint")}
              </LinkNavigation>
            </LinkNavigationGroup>
          </Accordion>
          {!config.isApp && (
            <LinkCell
              element="a"
              href={
                isPC(config.brand_name, config.region)
                  ? `${config.basePath}account/favorites.html`
                  : `${config.basePath}account/favorites`
              }
              id="wish list"
              label={t("text_wishlist")}
              variant="small"
            />
          )}
          {!config.isApp && (
            <LinkCell
              element="a"
              href={
                isPC(config.brand_name, config.region)
                  ? `${config.basePath}account/info.html`
                  : `${config.basePath}account`
              }
              id="account"
              label={t("text_membership_info")}
              variant="small"
            />
          )}
          {SB_ENV(config.brand_name, config.region).showAboutApp && (
            <LinkCell
              element="a"
              href={getAboutAppURL(config.region, config.language)}
              id="stylehint app"
              label={t("text_stylehintapp")}
              variant="small"
            />
          )}
          {!config.isApp &&
            config.brand_name === BRAND_LIST.UQ &&
            config.region.toString() === COUNTRY.HK && (
              <>
                <Keyline className="fr-ec-mt-spacing-03 fr-ec-mb-spacing-05" />
                <LinkNavigationGroup
                  variant="horizontal"
                  labelId={t("text_styling_book_header_title")}
                >
                  <LinkNavigation
                    variant="TitleSmall"
                    element="button"
                    onClick={() => handleSwitchLanguage(LANGUAGE.ZHHK)}
                    selected={config.language === LANGUAGE.ZHHK}
                  >
                    {t("text_locale_hk_zh")}
                  </LinkNavigation>

                  <LinkNavigation
                    variant="TitleSmall"
                    element="button"
                    onClick={() => handleSwitchLanguage(LANGUAGE.ENGB)}
                    selected={config.language === LANGUAGE.ENGB}
                  >
                    {t("text_locale_en")}
                  </LinkNavigation>
                </LinkNavigationGroup>
              </>
            )}
        </React.Fragment>
      </Drawer>
    </div>
  );
};

export default PublicHeader;
